// Country names (in every locale proef supports) for the wine countries druif holds; anything else
// falls back to the ISO code. Cards carry these codes, so the list lives in the domain; the flags
// are vector glyphs in the PWA's lib/flags.tsx, keyed by the same codes.

/** The locales proef's wording exists in; the PWA's `Locale` is this same union. */
export const LEXICON_LOCALES = ["nl", "en", "de", "fr", "it", "es", "pt"] as const;
export type LexiconLocale = (typeof LEXICON_LOCALES)[number];

export type Country = Readonly<Record<LexiconLocale, string>>;

export const COUNTRIES: Readonly<Record<string, Country>> = {
  FR: {
    nl: "Frankrijk",
    en: "France",
    de: "Frankreich",
    fr: "France",
    it: "Francia",
    es: "Francia",
    pt: "França",
  },
  IT: {
    nl: "Italië",
    en: "Italy",
    de: "Italien",
    fr: "Italie",
    it: "Italia",
    es: "Italia",
    pt: "Itália",
  },
  ES: {
    nl: "Spanje",
    en: "Spain",
    de: "Spanien",
    fr: "Espagne",
    it: "Spagna",
    es: "España",
    pt: "Espanha",
  },
  DE: {
    nl: "Duitsland",
    en: "Germany",
    de: "Deutschland",
    fr: "Allemagne",
    it: "Germania",
    es: "Alemania",
    pt: "Alemanha",
  },
  PT: {
    nl: "Portugal",
    en: "Portugal",
    de: "Portugal",
    fr: "Portugal",
    it: "Portogallo",
    es: "Portugal",
    pt: "Portugal",
  },
  AT: {
    nl: "Oostenrijk",
    en: "Austria",
    de: "Österreich",
    fr: "Autriche",
    it: "Austria",
    es: "Austria",
    pt: "Áustria",
  },
  NL: {
    nl: "Nederland",
    en: "Netherlands",
    de: "Niederlande",
    fr: "Pays-Bas",
    it: "Paesi Bassi",
    es: "Países Bajos",
    pt: "Países Baixos",
  },
  BE: {
    nl: "België",
    en: "Belgium",
    de: "Belgien",
    fr: "Belgique",
    it: "Belgio",
    es: "Bélgica",
    pt: "Bélgica",
  },
  CH: {
    nl: "Zwitserland",
    en: "Switzerland",
    de: "Schweiz",
    fr: "Suisse",
    it: "Svizzera",
    es: "Suiza",
    pt: "Suíça",
  },
  GR: {
    nl: "Griekenland",
    en: "Greece",
    de: "Griechenland",
    fr: "Grèce",
    it: "Grecia",
    es: "Grecia",
    pt: "Grécia",
  },
  HU: {
    nl: "Hongarije",
    en: "Hungary",
    de: "Ungarn",
    fr: "Hongrie",
    it: "Ungheria",
    es: "Hungría",
    pt: "Hungria",
  },
  GB: {
    nl: "Verenigd Koninkrijk",
    en: "United Kingdom",
    de: "Vereinigtes Königreich",
    fr: "Royaume-Uni",
    it: "Regno Unito",
    es: "Reino Unido",
    pt: "Reino Unido",
  },
  US: {
    nl: "Verenigde Staten",
    en: "United States",
    de: "Vereinigte Staaten",
    fr: "États-Unis",
    it: "Stati Uniti",
    es: "Estados Unidos",
    pt: "Estados Unidos",
  },
  AR: {
    nl: "Argentinië",
    en: "Argentina",
    de: "Argentinien",
    fr: "Argentine",
    it: "Argentina",
    es: "Argentina",
    pt: "Argentina",
  },
  CL: {
    nl: "Chili",
    en: "Chile",
    de: "Chile",
    fr: "Chili",
    it: "Cile",
    es: "Chile",
    pt: "Chile",
  },
  ZA: {
    nl: "Zuid-Afrika",
    en: "South Africa",
    de: "Südafrika",
    fr: "Afrique du Sud",
    it: "Sudafrica",
    es: "Sudáfrica",
    pt: "África do Sul",
  },
  AU: {
    nl: "Australië",
    en: "Australia",
    de: "Australien",
    fr: "Australie",
    it: "Australia",
    es: "Australia",
    pt: "Austrália",
  },
  NZ: {
    nl: "Nieuw-Zeeland",
    en: "New Zealand",
    de: "Neuseeland",
    fr: "Nouvelle-Zélande",
    it: "Nuova Zelanda",
    es: "Nueva Zelanda",
    pt: "Nova Zelândia",
  },
  GE: {
    nl: "Georgië",
    en: "Georgia",
    de: "Georgien",
    fr: "Géorgie",
    it: "Georgia",
    es: "Georgia",
    pt: "Geórgia",
  },
  MX: {
    nl: "Mexico",
    en: "Mexico",
    de: "Mexiko",
    fr: "Mexique",
    it: "Messico",
    es: "México",
    pt: "México",
  },
  UY: {
    nl: "Uruguay",
    en: "Uruguay",
    de: "Uruguay",
    fr: "Uruguay",
    it: "Uruguay",
    es: "Uruguay",
    pt: "Uruguai",
  },
  BR: {
    nl: "Brazilië",
    en: "Brazil",
    de: "Brasilien",
    fr: "Brésil",
    it: "Brasile",
    es: "Brasil",
    pt: "Brasil",
  },
  RO: {
    nl: "Roemenië",
    en: "Romania",
    de: "Rumänien",
    fr: "Roumanie",
    it: "Romania",
    es: "Rumanía",
    pt: "Roménia",
  },
  BG: {
    nl: "Bulgarije",
    en: "Bulgaria",
    de: "Bulgarien",
    fr: "Bulgarie",
    it: "Bulgaria",
    es: "Bulgaria",
    pt: "Bulgária",
  },
  HR: {
    nl: "Kroatië",
    en: "Croatia",
    de: "Kroatien",
    fr: "Croatie",
    it: "Croazia",
    es: "Croacia",
    pt: "Croácia",
  },
  SI: {
    nl: "Slovenië",
    en: "Slovenia",
    de: "Slowenien",
    fr: "Slovénie",
    it: "Slovenia",
    es: "Eslovenia",
    pt: "Eslovénia",
  },
  CZ: {
    nl: "Tsjechië",
    en: "Czechia",
    de: "Tschechien",
    fr: "Tchéquie",
    it: "Cechia",
    es: "Chequia",
    pt: "Chéquia",
  },
  LU: {
    nl: "Luxemburg",
    en: "Luxembourg",
    de: "Luxemburg",
    fr: "Luxembourg",
    it: "Lussemburgo",
    es: "Luxemburgo",
    pt: "Luxemburgo",
  },
  CA: {
    nl: "Canada",
    en: "Canada",
    de: "Kanada",
    fr: "Canada",
    it: "Canada",
    es: "Canadá",
    pt: "Canadá",
  },
  IL: {
    nl: "Israël",
    en: "Israel",
    de: "Israel",
    fr: "Israël",
    it: "Israele",
    es: "Israel",
    pt: "Israel",
  },
  LB: {
    nl: "Libanon",
    en: "Lebanon",
    de: "Libanon",
    fr: "Liban",
    it: "Libano",
    es: "Líbano",
    pt: "Líbano",
  },
  MD: {
    nl: "Moldavië",
    en: "Moldova",
    de: "Moldau",
    fr: "Moldavie",
    it: "Moldavia",
    es: "Moldavia",
    pt: "Moldávia",
  },
  TR: {
    nl: "Turkije",
    en: "Turkey",
    de: "Türkei",
    fr: "Turquie",
    it: "Turchia",
    es: "Turquía",
    pt: "Turquia",
  },
};

/** Every code the lexicon names, upper case. */
export const COUNTRY_CODES: readonly string[] = Object.keys(COUNTRIES);

export function isCountryCode(code: string): boolean {
  return Object.hasOwn(COUNTRIES, code);
}

/** A country's name in `locale`; the upper-cased code for one the lexicon does not hold. */
export function localizedCountryName(code: string | undefined, locale: LexiconLocale): string {
  if (code === undefined || code === "") return "";
  const upper = code.toUpperCase();
  return COUNTRIES[upper]?.[locale] ?? upper;
}
